import { useMemo } from 'react';

import { motion } from 'motion/react';

import { useGetTeamList } from '../../query/useGetTeamList';
import { TeamMember } from './TeamMember';

// Import team member images

// X (Twitter) icon component
function XIcon({ className, style }) {
  return (
    <svg
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const BOSSS_BLUE = '#1e638d';


export default function Team() {
  // import.meta.glob is synchronous and stable for the lifetime of the bundle, so derive the image map once via useMemo rather than useState + useEffect (avoids the cascading-render that react-hooks/set-state-in-effect flags).
  const teamImages = useMemo(() => {
    const imageModules = import.meta.glob('../../assets/team/*.{png,jpg,jpeg,webp,svg}', { eager: true });
    const imageUrls = {};

    Object.values(imageModules).forEach((mod) => {
      const path = mod.default;
      const fileName = path.split('/').pop().split('.')[0]?.replace(/-[^-]+$/, '');

      imageUrls[fileName] = { src: path, lastName: fileName };
    });

    return imageUrls;
  }, []);

  const { data: teamMembers, isLoading, error } = useGetTeamList();

  if (isLoading) {
    return (
      <div className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Loading team member list...
            </h3>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Error loading team member list:
              {' '}
              {error ? error : 'unidentified error'}
            </h3>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <div
              style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              Our Leadership
            </div>

            <h1
              style={{ color: '#1e293b' }}
              className="text-4xl lg:text-5xl font-bold mb-6"
            >
              Meet the Team
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our success is our people. Our founders are Cybersecurity pioneers with decades of experience in Cybersecurity, Technology, and Risk Management.
            </p>
          </motion.div>

          {/* Team Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {teamMembers && teamMembers.length > 0
              ? teamMembers.map((member, index) => (
                <TeamMember key={index} index={index} XIcon={XIcon} member={member} teamImages={teamImages} BOSSS_BLUE={BOSSS_BLUE} />
              ))
              : <div className="md:col-span-2 flex justify-center items-center w-full">
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  initial={{ opacity: 0, y: 40 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="bg-white w-full max-w-md rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <p className="text-xl text-center py-12 text-gray-600 leading-relaxed">
                    No team members found
                  </p>
                </motion.div>
              </div>}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: '#1e293b' }}
              className="text-3xl font-bold mb-4"
            >
              Our Mission
            </h2>

            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We&apos;re on a mission to democratize enterprise security. BOSSS makes sophisticated security operations accessible to organizations of all sizes, empowering teams to protect what matters most.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Innovation',
                description: 'Pushing boundaries with cutting-edge AI and automation to stay ahead of evolving threats.',
              },
              {
                title: 'Integrity',
                description: 'Building trust through transparency, ethical practices, and unwavering commitment to security.',
              },
              {
                title: 'Impact',
                description: 'Delivering measurable outcomes that transform how organizations approach cybersecurity.',
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                viewport={{ once: true }}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h3
                  style={{ color: BOSSS_BLUE }}
                  className="text-xl font-bold mb-3"
                >
                  {value.title}
                </h3>

                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
